import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// ClarityStack Intake Form v2.1 — All mandatory fields marked with asterisk
const IntakeForm = () => {
  const { therapistId } = useParams();
  const [therapistInfo, setTherapistInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    phone: '',
    occupation: '',
    primary_concern: '',
    previous_therapy: '',
    medications: '',
    source_of_lead: '',
    source_of_lead_other: '',
    consent_ai_notes: false,
    consent_whatsapp: false
  });

  useEffect(() => {
    fetchTherapistInfo();
  }, [therapistId]);

  const fetchTherapistInfo = async () => {
    try {
      const response = await fetch(`${API}/intake/${therapistId}/info`);
      if (response.ok) {
        setTherapistInfo(await response.json());
      } else {
        setError('Therapist not found');
      }
    } catch (err) {
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_ai_notes && !formData.consent_whatsapp) {
      toast.error('Please provide at least one consent to proceed');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/intake/${therapistId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          age: formData.age ? parseInt(formData.age) : null
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Submission failed');
      }

      const result = await response.json();
      if (result.whatsapp_number) {
        setWhatsappNumber(result.whatsapp_number);
      }
      setSubmitted(true);
      toast.success('Registration successful!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Form Not Available</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const prefilled = encodeURIComponent('Hi, I just submitted my details.');
    const waLink = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${prefilled}` : '';

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h2 
              className="text-2xl font-semibold text-foreground mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Thank You!
            </h2>
            <p className="text-muted-foreground mb-6">
              Your information has been submitted successfully.
            </p>

            {waLink ? (
              <>
                <p className="text-sm text-foreground mb-4">
                  Tap the button below to book your appointment on WhatsApp:
                </p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-booking-btn"
                  className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-xl text-white font-medium text-base"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Book Your Appointment on WhatsApp
                </a>
                <p className="text-xs text-muted-foreground mt-4">
                  This will open WhatsApp with a pre-filled message to get you started.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {therapistInfo?.name} will reach out to you soon.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header - White-labeled for therapist */}
        <div className="text-center mb-8">
          <h1 
            className="text-2xl sm:text-3xl font-semibold text-foreground mb-2"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Patient Intake Form
          </h1>
          {therapistInfo && (
            <div className="text-muted-foreground">
              <p className="text-lg font-medium text-foreground">{therapistInfo.name}</p>
              {therapistInfo.qualification && (
                <p className="text-sm mt-1">{therapistInfo.qualification}</p>
              )}
            </div>
          )}
        </div>

        {/* Form */}
        <Card className="border-border" data-testid="intake-form">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Fraunces, serif' }}>
              Your Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      required
                      data-testid="intake-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      required
                      data-testid="intake-age-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    required
                    data-testid="intake-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Required for calendar invites
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (WhatsApp) *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    placeholder="+91 98765 43210"
                    required
                    data-testid="intake-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="occupation">Occupation *</Label>
                  <Input
                    id="occupation"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    placeholder="e.g., Software Engineer, Teacher, Student"
                    required
                    data-testid="intake-occupation-input"
                  />
                </div>

                {/* Source of Lead */}
                <div className="space-y-2">
                  <Label htmlFor="source_of_lead">How did you hear about us? *</Label>
                  <select
                    id="source_of_lead"
                    name="source_of_lead"
                    value={formData.source_of_lead}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 rounded-xl border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    required
                    data-testid="intake-source-lead-select"
                  >
                    <option value="">Select an option</option>
                    <option value="referral">Referral from someone</option>
                    <option value="online_search">Online Search (Google, etc.)</option>
                    <option value="instagram">Instagram</option>
                    <option value="returning_client">Returning Client</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Other - Text field if source_of_lead is "other" */}
                {formData.source_of_lead === 'other' && (
                  <div className="space-y-2">
                    <Label htmlFor="source_of_lead_other">Please specify</Label>
                    <Input
                      id="source_of_lead_other"
                      name="source_of_lead_other"
                      value={formData.source_of_lead_other}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="How did you find us?"
                      data-testid="intake-source-lead-other"
                    />
                  </div>
                )}
              </div>

              {/* Clinical Info */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-medium text-foreground">Clinical Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="primary_concern">What brings you to therapy? *</Label>
                  <Textarea
                    id="primary_concern"
                    name="primary_concern"
                    value={formData.primary_concern}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    placeholder="Please describe your primary concern or reason for seeking therapy..."
                    required
                    data-testid="intake-concern-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="previous_therapy">Previous Therapy Experience</Label>
                  <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={formData.previous_therapy === 'NA'}
                        onChange={(e) => setFormData(prev => ({ ...prev, previous_therapy: e.target.checked ? 'NA' : '' }))}
                        className="rounded"
                        data-testid="previous-therapy-na"
                      />
                      Not applicable
                    </label>
                  </div>
                  {formData.previous_therapy !== 'NA' && (
                    <Textarea
                      id="previous_therapy"
                      name="previous_therapy"
                      value={formData.previous_therapy}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="Have you been to therapy before? If yes, please share briefly..."
                      data-testid="previous-therapy-input"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medications">Current Medications</Label>
                  <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={formData.medications === 'NA'}
                        onChange={(e) => setFormData(prev => ({ ...prev, medications: e.target.checked ? 'NA' : '' }))}
                        className="rounded"
                        data-testid="medications-na"
                      />
                      Not applicable
                    </label>
                  </div>
                  {formData.medications !== 'NA' && (
                    <Textarea
                      id="medications"
                      name="medications"
                      value={formData.medications}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="List any medications you're currently taking..."
                      data-testid="medications-input"
                    />
                  )}
                </div>
              </div>

              {/* Consent */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-medium text-foreground">Consent *</h3>
                <p className="text-xs text-muted-foreground -mt-2">At least one consent is required to proceed</p>
                
                <div className="space-y-4 p-4 rounded-xl bg-muted/50">
                  <label 
                    htmlFor="consent_ai_notes" 
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <Checkbox
                      id="consent_ai_notes"
                      name="consent_ai_notes"
                      checked={formData.consent_ai_notes}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, consent_ai_notes: checked }))
                      }
                      className="mt-1"
                      data-testid="consent-ai-checkbox"
                    />
                    <div>
                      <span className="font-medium text-foreground">
                        AI-Assisted Notes
                      </span>
                      <p className="text-sm text-muted-foreground mt-1">
                        I consent to the use of AI tools to assist my therapist in 
                        documentation and session notes. All AI-generated content is 
                        reviewed by my therapist.
                      </p>
                    </div>
                  </label>

                  <label 
                    htmlFor="consent_whatsapp" 
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <Checkbox
                      id="consent_whatsapp"
                      name="consent_whatsapp"
                      checked={formData.consent_whatsapp}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, consent_whatsapp: checked }))
                      }
                      className="mt-1"
                      data-testid="consent-whatsapp-checkbox"
                    />
                    <div>
                      <span className="font-medium text-foreground">
                        WhatsApp Communication
                      </span>
                      <p className="text-sm text-muted-foreground mt-1">
                        I consent to receiving communication from my therapist 
                        via WhatsApp for scheduling and follow-ups.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90"
                disabled={submitting}
                data-testid="submit-intake-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Registration'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Your information is securely stored and only accessible to your therapist.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntakeForm;
